<template>
  <div class="ai-generate-mode-panel ai-generate-single">
    <div class="ai-gen-section">
      <div class="ai-gen-section-title"><span class="required-star">*</span>生图提示词 (Prompt)</div>
      <Input v-model="prompt" type="textarea" :rows="4" :disabled="generating" placeholder="例如：将台灯放在现代简约风格的床头柜上，柔和暖光，大理石背景，超高清..." />
    </div>

    <div class="ai-gen-section">
      <div class="ai-gen-section-head">
        <div class="ai-gen-section-title">
          参考图片与生成模式
          <span class="ai-gen-hint">（支持拖拽调整排序；点击卡片下方指定处理方式）</span>
        </div>
        <div v-if="referenceImages.length > 0" class="ai-gen-ref-summary">已选 {{ referenceImages.length }} 张（{{ aiGenCount }} 张AI处理，{{ keepCount }} 张保留原图）</div>
      </div>

      <div class="ai-gen-ref-box">
        <draggable
          v-model="referenceImages"
          :disabled="generating"
          :animation="200"
          ghost-class="ai-gen-drag-ghost"
          chosen-class="ai-gen-drag-chosen"
          drag-class="ai-gen-dragging"
          class="ai-gen-ref-list"
          :filter="'.no-drag'"
          :prevent-on-filter="false"
        >
          <div v-for="(img, index) in referenceImages" :key="getImageKey(img)" class="ai-gen-ref-item" :title="getRefImageTooltip(img)">
            <div class="ai-gen-ref-thumb">
              <img :src="filenodeViewTargetUrl + img.path" />
              <div class="ai-gen-ref-badges">
                <span class="ai-gen-ref-order">{{ index + 1 }}</span>
                <span v-if="img.sourceType === 'upload'" class="ai-gen-ref-badge is-upload">本地</span>
                <template v-else>
                  <span class="ai-gen-ref-badge is-lang">{{ getLangLabel(img.language) }}</span>
                  <span v-if="dimension === 'sku' && img.setName" class="ai-gen-ref-badge is-set" :style="{ background: getSetTypeBadgeColor(img.setType) }">
                    {{ img.setName }}
                  </span>
                </template>
              </div>
              <div class="ai-gen-ref-cover">
                <Icon type="ios-trash-outline" title="移除" @click.native.stop="removeReferenceImage(index)" />
              </div>
            </div>
            <div class="ai-gen-mode-btns">
              <button type="button" :class="{ active: getGenMode(img) === 'ai' }" :disabled="generating" @click="setGenMode(img, 'ai')">AI生图</button>
              <button type="button" :class="{ active: getGenMode(img) === 'keep' }" :disabled="generating" @click="setGenMode(img, 'keep')">保留原图</button>
            </div>
          </div>

          <dytUpload
            v-if="!generating"
            ref="uploadRef"
            class="ai-gen-upload no-drag"
            file-name="files"
            :headers="uploadFilesHeader"
            :max-size="uploadFilesMaxSize"
            :on-exceeded-size="handleExceedSize"
            :show-upload-list="false"
            :format="['jpg', 'png', 'gif']"
            accept="image/*"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :on-format-error="handleFormatError"
            multiple
            :before-upload="beforeUpload"
            :action="uploadFilesUrl"
          >
            <div class="ai-gen-add-item">
              <Icon type="ios-folder-outline" size="38" color="#bbb" />
              <div>上传本地图片</div>
              <span>支持多选文件</span>
            </div>
          </dytUpload>

          <div v-if="!generating" class="ai-gen-add-item no-drag" @click="openSelectImageModal">
            <Icon type="ios-images-outline" size="38" color="#bbb" />
            <div>选用已有图案</div>
            <span>从图库中批量选择</span>
          </div>
        </draggable>
      </div>
    </div>

    <aiSelectImageModal
      v-model="selectImageModalVisible"
      :dimension="dimension"
      :multi-lang-image-data="multiLangImageData"
      :sku-image-set-list="skuImageSetList"
      :pre-selected="existingImagesPreSelected"
      :filenode-view-target-url="filenodeViewTargetUrl"
      @on-ok="handleSelectImageOk"
    />
  </div>
</template>

<script>
import api from "@/api/api";
import aiSelectImageModal from "./aiSelectImageModal";
import draggable from "vuedraggable";

export default {
  name: "aiGenerateSingleModal",
  components: {
    aiSelectImageModal,
    draggable,
  },
  props: {
    dimension: {
      type: String,
      default: "spu",
    },
    multiLangImageData: {
      type: Array,
      default: () => [],
    },
    skuImageSetList: {
      type: Array,
      default: () => [],
    },
    filenodeViewTargetUrl: {
      type: String,
      default: "",
    },
    generating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      prompt: "",
      referenceImages: [],
      selectImageModalVisible: false,
      uploadFilesUrl: api.upload_files + "?basePath=/product",
      uploadFilesMaxSize: 10240,
      uploadMaxLength: 20,
      uploadFilesList: [],
      uploadFilesTime: null,
      uploadVersion: 0,
    };
  },
  computed: {
    uploadFilesHeader() {
      return {
        ...this.$store.getters.erpRequestHeaders,
        ...this.$store.getters.dytRequestHeaders,
      };
    },
    existingImagesPreSelected() {
      return this.referenceImages.filter((item) => item.sourceType !== "upload");
    },
    aiGenCount() {
      return this.referenceImages.filter((item) => this.getGenMode(item) === "ai").length;
    },
    keepCount() {
      return this.referenceImages.filter((item) => this.getGenMode(item) === "keep").length;
    },
  },
  methods: {
    resetForm() {
      this.uploadVersion += 1;
      if (this.uploadFilesTime) clearTimeout(this.uploadFilesTime);
      this.prompt = "";
      this.referenceImages = [];
      this.selectImageModalVisible = false;
      this.uploadFilesList = [];
    },
    getImageKey(item) {
      if (item.imageId !== undefined && item.imageId !== null && item.imageId !== "") {
        return `${this.dimension}-id-${item.imageId}`;
      }
      return `${this.dimension}-${item.productGoodsImageSetId || ""}-${item.language || "common"}-${item.path}`;
    },
    openSelectImageModal() {
      this.selectImageModalVisible = true;
    },
    handleSelectImageOk(selectedList) {
      const oldModes = {};
      this.referenceImages.forEach((item) => {
        oldModes[this.getImageKey(item)] = this.getGenMode(item);
      });
      const uploadImages = this.referenceImages.filter((item) => item.sourceType === "upload");
      const selectedImages = (selectedList || []).map((item) => {
        const key = this.getImageKey(item);
        return {
          ...item,
          genMode: oldModes[key] || "ai",
        };
      });
      this.referenceImages = uploadImages.concat(selectedImages);
    },
    getGenMode(item) {
      return item.genMode === "keep" ? "keep" : "ai";
    },
    setGenMode(item, mode) {
      if (!this.generating) this.$set(item, "genMode", mode);
    },
    removeReferenceImage(index) {
      this.referenceImages.splice(index, 1);
    },
    getRefImageTooltip(item) {
      if (item.sourceType === "upload") return "来源：本地上传";
      const lang = this.getLangLabel(item.language);
      if (this.dimension === "sku" && item.setName) {
        const type = this.getSetTypeLabel(item.setType);
        return `来源：已有图案 / ${lang} / ${item.setName}${type ? ` (${type})` : ""}`;
      }
      return `来源：已有图案 / ${lang}`;
    },
    getLangLabel(lang) {
      const map = {
        common: "通用",
        en: "EN",
        fr: "FR",
        de: "DE",
        ru: "RU",
        nl: "NL",
      };
      return map[lang] || lang || "";
    },
    getSetTypeLabel(setType) {
      const map = { 1: "网图", 2: "非实拍自修", 3: "实拍自修" };
      return map[String(setType)] || String(setType || "");
    },
    getSetTypeBadgeColor(setType) {
      const map = { 1: "#f90", 2: "#19be6b", 3: "#2d8cf0" };
      return map[String(setType)] || "#999";
    },
    handleExceedSize() {
      this.$Message.error("文件大小超出限制，最大为10M");
    },
    handleUploadError() {
      this.$Message.error("系统繁忙，请重新尝试");
    },
    handleFormatError() {
      this.$Message.error("文件格式有误，仅支持 jpg / png / gif");
    },
    handleUploadSuccess() {},
    beforeUpload(file) {
      const uploadRef = this.$refs.uploadRef;
      const input = uploadRef && uploadRef.$refs && uploadRef.$refs.input;
      const selectedFiles = input ? input.files : [];
      const currentIndex = Array.prototype.indexOf.call(selectedFiles, file);
      if (currentIndex === 0 && selectedFiles.length > this.uploadMaxLength) {
        this.$Message.error(`文件数量超出限制，一次最多可上传${this.uploadMaxLength}张`);
        return false;
      }
      if (selectedFiles.length > this.uploadMaxLength) return false;

      const form = new FormData();
      form.append("files", file);
      this.uploadFilesList.push(form);
      if (this.uploadFilesTime) clearTimeout(this.uploadFilesTime);
      this.uploadFilesTime = setTimeout(() => this.uploadFilesListHand(), 300);
      return false;
    },
    uploadFilesListHand() {
      if (this.uploadFilesList.length === 0) return;
      const version = this.uploadVersion;
      const awaitList = this.uploadFilesList.slice();
      this.uploadFilesList = [];
      Promise.all(
        awaitList.map((form) => {
          return this.axios.post(`${this.uploadFilesUrl}&random=${Math.random().toString().substring(2)}`, form);
        }),
      )
        .then((list) => {
          if (version !== this.uploadVersion) return;
          list.forEach((res) => {
            if (res && res.data && Array.isArray(res.data.datas)) {
              res.data.datas.forEach((path) => {
                this.referenceImages.push({
                  path,
                  sourceType: "upload",
                  genMode: "ai",
                });
              });
            }
          });
        })
        .catch(() => {
          if (version === this.uploadVersion) this.$Message.error("上传失败，请重试");
        });
    },
    validate() {
      if (!this.prompt || !this.prompt.trim()) {
        this.$Message.error("请输入生图提示词");
        return false;
      }
      if (this.referenceImages.length === 0) {
        this.$Message.error("请至少选择一张参考图片");
        return false;
      }
      if (this.aiGenCount === 0) {
        this.$Message.error("参考图片中至少需要一张选择“AI生图”模式");
        return false;
      }
      return true;
    },
    getFormData() {
      return {
        prompt: (this.prompt || "").trim(),
        productAiGenerateImageDetailQOList: this.referenceImages.map((item, index) => {
          const data = {
            sortNo: index + 1,
            generateType: this.getGenMode(item) === "ai" ? 1 : 2,
          };
          if (item.imageId !== undefined && item.imageId !== null && item.imageId !== "") {
            data.imageId = item.imageId;
          } else {
            data.localImagePath = item.path;
          }
          return data;
        }),
      };
    },
  },
};
</script>

<style scoped lang="less">
.ai-gen-section {
  margin-bottom: 20px;
}

.ai-gen-section-title {
  margin-bottom: 10px;
  color: #17233d;
  font-size: 14px;
  font-weight: 600;

  .required-star {
    margin-right: 4px;
    color: #ed4014;
  }

  .ai-gen-hint {
    margin-left: 4px;
    color: #999;
    font-size: 12px;
    font-weight: normal;
  }
}

.ai-gen-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.ai-gen-ref-summary {
  flex-shrink: 0;
  margin-left: 8px;
  color: #2d8cf0;
  font-size: 12px;
  white-space: nowrap;
}

.ai-gen-ref-box {
  min-height: 184px;
  padding: 16px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
}

.ai-gen-ref-list {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
}

.ai-gen-ref-item {
  width: 120px;
  overflow: hidden;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
  cursor: move;
}

.ai-gen-ref-thumb {
  position: relative;
  height: 120px;
  overflow: hidden;
  background: #f8f8f9;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ai-gen-ref-badges {
  position: absolute;
  top: 4px;
  left: 4px;
  right: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  pointer-events: none;
}

.ai-gen-ref-order,
.ai-gen-ref-badge {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 8px;
  color: #fff;
  font-size: 11px;
  line-height: 16px;
}

.ai-gen-ref-order {
  min-width: 18px;
  background: rgba(23, 35, 61, 0.65);
  text-align: center;
}

.ai-gen-ref-badge.is-upload {
  background: rgba(144, 147, 153, 0.9);
}

.ai-gen-ref-badge.is-lang {
  background: #19be6b;
}

.ai-gen-ref-badge.is-set {
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-gen-ref-cover {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  opacity: 0;
  transition: opacity 0.2s;

  i {
    padding: 4px 8px;
    font-size: 26px;
    cursor: pointer;
  }
}

.ai-gen-ref-thumb:hover .ai-gen-ref-cover {
  opacity: 1;
}

.ai-gen-mode-btns {
  display: flex;
  border-top: 1px solid #dcdee2;

  button {
    flex: 1;
    padding: 6px 0;
    border: 0;
    background: #f8f8f9;
    color: #515a6e;
    font-size: 12px;
    cursor: pointer;

    & + button {
      border-left: 1px solid #dcdee2;
    }

    &.active {
      background: #2d8cf0;
      color: #fff;
    }
  }
}

.ai-gen-add-item {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 152px;
  border: 1px dashed #c0c4cc;
  border-radius: 4px;
  color: #666;
  cursor: pointer;

  &:hover {
    border-color: #2d8cf0;
    color: #2d8cf0;
  }

  span {
    margin-top: 5px;
    color: #999;
    font-size: 11px;
  }
}

.ai-gen-drag-ghost {
  opacity: 0.4;
}

.ai-gen-drag-chosen {
  opacity: 0.9;
}
</style>

<style lang="less">
.ai-generate-modal .ai-gen-upload .ivu-upload {
  display: inline-block;
}
</style>
